<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { computed, onMounted, reactive, watch } from "vue"
import upperCase from "lodash/upperCase"
import InputLabel from "@/Components/InputLabel.vue"
import TextInput from "@/Components/TextInput.vue"
import PrimaryButton from "@/Components/PrimaryButton.vue"
import { VALIDATION_TYPES, getValidationHint, validateInput } from "@/Helpers/ValidationHelper.js"

const emits = defineEmits(['close', 'updated', 'clear'])

const props = defineProps({
    propertyKey: {
        type: String,
        default: null
    },
    propertyValue: {
        type: String,
        default: null
    },
    propertyIndex: {
        type: Number,
        default: null
    },
    item: {
        type: Object,
        default: null
    },
    validationType: {
        type: String,
        default: 'text',
        validator: (value) => ['text', 'number', 'dice'].includes(value)
    },
    showClear: {
        type: Boolean,
        default: false
    },
    minValue: {
        type: Number,
        default: null
    },
    maxValue: {
        type: Number,
        default: null
    },
    onUpdated: {
        type: Function,
        default: null
    },
    onClose: {
        type: Function,
        default: null
    }
})

const data = reactive({
    propertyValue: null,
    validationError: null,
    isValid: true,
    updatedItem: null
})

const prettyKey = computed(() => {
    let name = props.propertyKey.replace(/_/g, ' ')
    return upperCase(name)
})

const validationHint = computed(() => {
    return getValidationHint(props.validationType, props.minValue, props.maxValue)
})

const isApplyDisabled = computed(() => {
    return !data.isValid || !data.propertyValue?.trim()
})

function validateInputValue(value) {
    const result = validateInput(value, props.validationType, props.minValue, props.maxValue)
    data.isValid = result.isValid
    data.validationError = result.error
}

function apply() {
    if (data.isValid && data.propertyValue?.trim()) {
        if (props.item) {
            // Legacy mode - update item property
            data.updatedItem[props.propertyKey] = data.propertyValue
            emits('updated', data.updatedItem)
        } else {
            // New mode - pass key/value/index
            emits('updated', {key: props.propertyKey, value: data.propertyValue, index: props.propertyIndex})
        }
        emits('close')
    }
}

function clearValue() {
    data.propertyValue = ''
    data.updatedItem[props.propertyKey] = ''

    emits('updated', data.updatedItem)
    emits('clear', {key: props.propertyKey, index: props.propertyIndex})
    emits('close')
}

watch(() => data.propertyValue, (newValue) => {
    validateInputValue(newValue)
})

onMounted(() => {
    if (props.item) {
        data.updatedItem = props.item
        data.propertyValue = props.item[props.propertyKey]
    } else {
        data.propertyValue = props.propertyValue
    }
    validateInputValue(data.propertyValue)
})
</script>

<template>
    <TransitionRoot as="template" :show="props.propertyKey != null">
        <Dialog as="div" class="relative z-50" @close="$emit('close')">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all border border-gray-700">
                            <div class="px-6 pt-6 pb-4">
                                <h3 class="text-lg font-semibold text-white mb-1">
                                    {{ prettyKey }}
                                </h3>
                                <div class="text-sm h-5 relative">
                                    <p
                                        class="text-gray-300 absolute inset-0 transition-opacity duration-200 ease-in-out"
                                        :class="{ 'opacity-100': !data.validationError, 'opacity-0': data.validationError }"
                                    >
                                        {{ validationHint }}
                                    </p>
                                    <p
                                        class="text-red-400 flex items-center absolute inset-0 transition-opacity duration-200 ease-in-out"
                                        :class="{ 'opacity-100': data.validationError, 'opacity-0': !data.validationError }"
                                    >
                                        <svg class="mr-1.5 h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zM9.25 14a.75.75 0 011.5 0v.01a.75.75 0 01-1.5 0V14z" clip-rule="evenodd" />
                                        </svg>
                                        {{ data.validationError }}
                                    </p>
                                </div>
                            </div>

                            <div class="px-6 pb-6">
                                <div class="space-y-4">
                                    <div>
                                        <textarea
                                            id="property"
                                            v-model="data.propertyValue"
                                            class="block w-full rounded-md px-3 py-2 text-white bg-gray-700 border border-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-colors"
                                            :class="{
                                                'border-red-500 focus:ring-red-500': !data.isValid && data.propertyValue?.trim(),
                                                'border-gray-600 focus:ring-blue-500': data.isValid || !data.propertyValue?.trim()
                                            }"
                                            rows="4"
                                            placeholder="Enter value..."
                                        />
                                    </div>

                                    <div class="flex items-center justify-between gap-3">
                                        <div class="flex gap-2">
                                            <button
                                                v-if="showClear"
                                                type="button"
                                                @click="clearValue"
                                                class="inline-flex items-center rounded-md bg-gray-700 px-3 py-2 text-sm font-medium text-gray-300 border border-gray-600 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                            >
                                                <svg class="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                                Clear
                                            </button>
                                        </div>

                                        <div class="flex gap-2">
                                            <button
                                                type="button"
                                                @click="$emit('close')"
                                                class="inline-flex items-center rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-gray-300 border border-gray-600 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                            >
                                                Cancel
                                            </button>

                                            <PrimaryButton
                                                @click="apply"
                                                :disabled="isApplyDisabled"
                                                class="!bg-blue-600 hover:!bg-blue-700 !text-white"
                                            >
                                                Apply
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

